import { Button, Col, DatePicker, Form, Input, Row, Select } from "antd";
import { useForm } from "antd/lib/form/Form";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ViewpointInput } from "./ViewpointInput";
import { dayjs } from "Config/dayjs";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { BCFStore, SubmitTopicRequest } from "Components/XKTViewer/Stores/BCFStore";

interface BcfCreateIssueFormProps extends ModalComponentProps {
  initialData?: SubmitTopicRequest;
  onSetAddViewpointMode: () => void;
  bcfStore: BCFStore;
  onSubmit?: (topic: API_BcfServerTopic) => void;
}

export const BcfCreateIssueForm = observer((props: BcfCreateIssueFormProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [form] = useForm();
  const [loading, setLoading] = React.useState(false);
  const isDirtyRef = React.useRef(false);

  React.useEffect(() => {
    props.setOnSave?.(() => form.submit());
  }, [form, props.setOnSave]);

  const handleValuesChange = React.useCallback(() => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      props.setConfirmOnCancel?.(true);
    }
  }, [props.setConfirmOnCancel]);

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      const formData = { ...values };
      if (formData.due_date) {
        formData.due_date = dayjs(formData.due_date).format("YYYY-MM-DD");
      }
      const topic = await props.bcfStore.submitIssue(formData);
      props.onSubmit?.(topic);
      props.setConfirmOnCancel?.(false);
      props.hideModal();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveInputData = () => {
    const values = form.getFieldsValue();
    const payload = JSON.stringify(values);
    props.bcfStore.storeFormData(payload);
  };

  const enableViewpointAddMode = async () => {
    saveInputData();
    await props.setConfirmOnCancel?.(false);
    props.hideModal();
    props.onSetAddViewpointMode();
  };

  React.useEffect(() => {
    if (props.initialData) {
      const payload = { ...props.initialData };
      if (props.initialData?.due_date) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload.due_date = dayjs(payload.due_date) as any;
      }
      form.setFieldsValue(payload);
    }
  }, []);

  return (
    <Form layout="vertical" onFinish={handleFinish} onValuesChange={handleValuesChange} form={form} id="bcf_form">
      <Row gutter={{ sm: 10, md: 20 }}>
        <Col sm={12} md={16} lg={18}>
          <Form.Item
            name="title"
            label={t("Title")}
            rules={[{ required: true, message: t("This field is required", { ns: I18N_NS.VALIDATION }) }]}
          >
            <Input autoFocus placeholder={t("Title")} />
          </Form.Item>
        </Col>
        <Col sm={12} md={8} lg={6}>
          <Form.Item name="topic_status" label={t("Status")}>
            <Select<string>
              placeholder={t("Status")}
              options={props.bcfStore.extensions?.topic_status?.map((item) => ({ label: item, value: item }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ sm: 10, md: 20 }}>
        <Col sm={12} md={8} lg={6}>
          <Form.Item name="topic_type" label={t("Type")}>
            <Select<string>
              placeholder={t("Type")}
              options={props.bcfStore.extensions?.topic_type?.map((item) => ({ label: item, value: item }))}
            />
          </Form.Item>
        </Col>
        <Col sm={12} md={8} lg={6}>
          <Form.Item name="assigned_to_id" label={t("Assigned to")}>
            <Select
              placeholder={t("Assigned to")}
              showSearch={{ filterOption: true }}
              options={props.bcfStore.projectUsers.slice()}
            />
          </Form.Item>
        </Col>
        <Col sm={12} md={8} lg={6}>
          <Form.Item name="due_date" label={t("Due date")}>
            <DatePicker
              placeholder={t("Due date")}
              format="L"
              style={{ width: "100%" }}
              disabledDate={(dayjsDate) => dayjsDate.isBefore(new Date(), "day")}
            />
          </Form.Item>
        </Col>
        <Col sm={12} md={8} lg={6}>
          <Form.Item name="priority" label={t("Priority")}>
            <Select<string>
              placeholder={t("Priority")}
              options={props.bcfStore.extensions?.priority?.map((item) => ({ label: item, value: item }))}
            />
          </Form.Item>
        </Col>
        <Col sm={12} md={8} lg={6}>
          <Form.Item name="stage" label={t("Stage")}>
            <Select<string> placeholder={t("Stage")} options={props.bcfStore.stageOptions} allowClear />
          </Form.Item>
        </Col>
        <Col sm={12} md={8} lg={6}>
          <Form.Item name="labels" label={t("Label")}>
            <Select<string[]> mode="multiple" placeholder={t("Label")} options={props.bcfStore.labelOptions} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="description" label={t("Description")}>
        <Input.TextArea autoSize={{ minRows: 8 }} placeholder={t("Description")} />
      </Form.Item>
      <Form.Item name="viewpoints" label={t("Viewpoints")}>
        <ViewpointInput onEnableViewpointAddMode={enableViewpointAddMode} />
      </Form.Item>
      <ButtonsRow justify="space-between">
        <Button onClick={props.hideModal}>{t("Cancel")}</Button>
        <Button type="primary" htmlType="submit" loading={loading}>
          {t("Create")}
        </Button>
      </ButtonsRow>
    </Form>
  );
});
